import React, { useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { PRODUCTS, COLLECTION_NOTES, getProductsByCategory } from '../data/products';
import Header from '../components/Header';

const CollectionPage = () => {
  const navigate = useNavigate();
  const { category } = useParams();
  const [activeFilter, setActiveFilter] = useState(category || 'all');

  const filteredProducts = useMemo(() => {
    if (activeFilter === 'all') {
      return PRODUCTS;
    }
    return getProductsByCategory(activeFilter);
  }, [activeFilter]);

  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
    if (filter === 'all') {
      navigate('/collection/all');
    } else {
      navigate(`/collection/${filter}`);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto text-center">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-sans text-xs tracking-[0.3em] uppercase text-zinc-500 mb-4"
          >
            {activeFilter === 'all' ? 'All Formulas' : COLLECTION_NOTES.find(n => n.id === activeFilter)?.name || 'Collection'}
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="font-serif text-4xl sm:text-5xl font-light text-zinc-50 mb-8"
          >
            {activeFilter === 'all' ? 'The Complete Collection' : `${COLLECTION_NOTES.find(n => n.id === activeFilter)?.name || ''} Series`}
          </motion.h1>
        </div>
      </section>

      {/* Filters */}
      <section className="px-6 md:px-12 lg:px-24 mb-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6">
            <button
              data-testid="filter-all"
              onClick={() => handleFilterChange('all')}
              className={`font-sans text-xs tracking-[0.2em] uppercase px-4 py-2 transition-all duration-300 ${
                activeFilter === 'all'
                  ? 'text-white border-b border-white'
                  : 'text-zinc-500 hover:text-white border-b border-transparent'
              }`}
            >
              All
            </button>
            {COLLECTION_NOTES.map((note) => (
              <button
                key={note.id}
                data-testid={`filter-${note.id}`}
                onClick={() => handleFilterChange(note.id)}
                className={`font-sans text-xs tracking-[0.2em] uppercase px-4 py-2 transition-all duration-300 ${
                  activeFilter === note.id
                    ? 'text-white border-b border-white'
                    : 'text-zinc-500 hover:text-white border-b border-transparent'
                }`}
              >
                {note.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="px-6 md:px-12 lg:px-24 pb-24">
        <div className="max-w-7xl mx-auto">
          {filteredProducts.length === 0 ? (
            <div className="text-center py-16">
              <p className="font-sans text-zinc-500">No formulas found in this category.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
              {filteredProducts.map((product, index) => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <div
                    data-testid={`product-card-${product.id}`}
                    onClick={() => navigate(`/product/${product.id}`)}
                    className="group cursor-pointer"
                  >
                    <div className="relative aspect-[3/4] bg-[#0A0A0A] border border-white/5 overflow-hidden mb-4 group-hover:border-white/20 transition-all duration-500">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                      {!product.inStock && (
                        <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                          <span className="font-sans text-xs tracking-[0.2em] uppercase text-zinc-400">
                            Sold Out
                          </span>
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    </div>
                    <p className="font-sans text-xs tracking-[0.2em] uppercase text-zinc-500 mb-1">
                      {product.subtitle}
                    </p>
                    <div className="flex items-center justify-between">
                      <h3 className="font-serif text-lg text-zinc-50 group-hover:text-white transition-colors">
                        {product.name}
                      </h3>
                      <p className="font-sans text-sm text-zinc-400">
                        ${product.price}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/5">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6">
          <p className="font-serif text-xl text-zinc-50">Secret 13</p>
          <p className="font-sans text-xs text-zinc-600">
            © 2025 Secret 13. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default CollectionPage;
